import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './FeedbackModal.css';
import SvgIcon from './SvgIcon';

// Default static definitions for offline/immediate readiness
const DEFAULT_QUESTIONNAIRES = [
  {
    id: 'reg_progress',
    title: 'LMS Registration Progress Check',
    category: 'Registration',
    lmsFunction: 'Learner Registration',
    targetRole: 'Anonymous',
    questions: [
      { id: 'reg_q1', order: 1, text: 'Were you able to complete the account registration process?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'reg_q2', order: 2, text: 'How easy was it to complete the registration form?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'reg_q3', order: 3, text: 'Were the registration instructions, validation messages, and submission status clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'reg_q4', order: 4, text: 'What difficulties did you encounter during registration, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'login_progress',
    title: 'LMS Login Progress Check',
    category: 'Login',
    lmsFunction: 'User Login',
    targetRole: 'All',
    questions: [
      { id: 'login_q1', order: 1, text: 'Were you able to log in to your account successfully?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'login_q2', order: 2, text: 'How easy was it to log in to the LMS?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'login_q3', order: 3, text: 'Were the login instructions and error or success messages clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'login_q4', order: 4, text: 'What difficulties did you encounter when logging in, or what would improve the login experience?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_progress',
    title: 'LMS Admin Dashboard Progress Check',
    category: 'Admin',
    lmsFunction: 'Admin Dashboard Overview',
    targetRole: 'Admin',
    questions: [
      { id: 'admin_q1', order: 1, text: 'Were you able to complete your intended administrative task?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'admin_q2', order: 2, text: 'How easy was it to navigate and use the Admin Dashboard?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'admin_q3', order: 3, text: 'Were the information, status indicators, and results of your administrative actions clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'admin_q4', order: 4, text: 'What difficulties did you encounter, or what would you improve about the Admin Dashboard?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_registration_mgmt',
    title: 'Registration Management Feedback',
    category: 'Admin',
    lmsFunction: 'Registration Management',
    targetRole: 'Admin',
    questions: [
      { id: 'adm_reg_q1', order: 1, text: 'Were you able to review and process the intended registration?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_reg_q2', order: 2, text: 'How easy was it to review and approve registrations?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'adm_reg_q3', order: 3, text: 'Were the registration details and verification statuses clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_reg_q4', order: 4, text: 'What difficulties did you encounter when managing registrations?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_account_updates',
    title: 'Account Update Approval Feedback',
    category: 'Admin',
    lmsFunction: 'Account Update Approval',
    targetRole: 'Admin',
    questions: [
      { id: 'adm_acc_q1', order: 1, text: 'Was it clear which email and sector changes were awaiting approval?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_acc_q2', order: 2, text: 'How easy was it to review and process account update requests?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'adm_acc_q3', order: 3, text: 'Were the change diffs, SLA deadlines, and audit logs clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_acc_q4', order: 4, text: 'What difficulties did you encounter when approving account updates?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_course_mgmt',
    title: 'Course & Session Management Feedback',
    category: 'Admin',
    lmsFunction: 'Course & Session Management',
    targetRole: 'Admin',
    questions: [
      { id: 'adm_crs_q1', order: 1, text: 'Were you able to configure the courses, sessions, or project tags needed?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_crs_q2', order: 2, text: 'How easy was it to create or update a course and its class sessions?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'adm_crs_q3', order: 3, text: 'Were the capacity rules, schedule dates, and trainer assignments clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_crs_q4', order: 4, text: 'What difficulties did you encounter when managing courses and sessions?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_audit_logs',
    title: 'Audit Log & Compliance Feedback',
    category: 'Admin',
    lmsFunction: 'Audit Logs',
    targetRole: 'Admin',
    questions: [
      { id: 'adm_aud_q1', order: 1, text: 'Were you able to find the activity records you needed using the available search and filters?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_aud_q2', order: 2, text: 'How easy was it to navigate and search audit logs?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'adm_aud_q3', order: 3, text: 'Were the actor details, timestamps, and action descriptions clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_aud_q4', order: 4, text: 'What difficulties did you encounter when reviewing audit logs?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'admin_reports',
    title: 'Reporting & Analytics Feedback',
    category: 'Admin',
    lmsFunction: 'Reporting',
    targetRole: 'Admin',
    questions: [
      { id: 'adm_rep_q1', order: 1, text: 'Were the report results and available export options clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_rep_q2', order: 2, text: 'How easy was it to generate and filter training reports?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'adm_rep_q3', order: 3, text: 'Did the report metrics meet your administrative reporting needs?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'adm_rep_q4', order: 4, text: 'What difficulties did you encounter when generating reports?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'trainer_progress',
    title: 'LMS Trainer Dashboard Progress Check',
    category: 'Trainer',
    lmsFunction: 'Trainer Dashboard Overview',
    targetRole: 'Trainer',
    questions: [
      { id: 'tr_q1', order: 1, text: 'Were you able to find and manage the class session needed for your task?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_q2', order: 2, text: 'How easy was it to navigate and use the Trainer Dashboard?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'tr_q3', order: 3, text: 'Were the session details, attendance status, and system feedback clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_q4', order: 4, text: 'What difficulties did you encounter while using the Trainer Dashboard, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'trainer_att_code',
    title: 'Trainer Attendance Code Feedback',
    category: 'Trainer',
    lmsFunction: 'Attendance Code Generation',
    targetRole: 'Trainer',
    questions: [
      { id: 'tr_code_q1', order: 1, text: 'Were you able to generate an attendance code for the intended class session?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_code_q2', order: 2, text: 'How easy was it to select a class session and generate its attendance code?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'tr_code_q3', order: 3, text: 'Were the attendance code\'s validity duration and expiration status clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_code_q4', order: 4, text: 'What difficulties did you encounter when generating or renewing an attendance code?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'trainer_att_tracker',
    title: 'Trainer Attendance Tracker Feedback',
    category: 'Trainer',
    lmsFunction: 'Attendance Tracker',
    targetRole: 'Trainer',
    questions: [
      { id: 'tr_trk_q1', order: 1, text: 'Were you able to identify which learners had checked in for the selected class session?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_trk_q2', order: 2, text: 'How easy was it to monitor learner attendance for a class session?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'tr_trk_q3', order: 3, text: 'Were the learner check-in statuses and attendance information clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_trk_q4', order: 4, text: 'What difficulties did you encounter when monitoring attendance, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'trainer_calendar',
    title: 'Training Calendar Usability Feedback',
    description: 'Please share your experience using the Training Calendar to help us evaluate its functionality and identify areas for improvement.',
    category: 'Trainer',
    lmsFunction: 'Training Calendar',
    targetRole: 'Trainer',
    questions: [
      { id: 'tr_cal_q1', order: 1, text: 'Does the Training Calendar work as expected when you view your scheduled training sessions?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'tr_cal_q2', order: 2, text: 'How easy is it to navigate the calendar and find the training sessions you need?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'tr_cal_q3', order: 3, text: 'Are the training dates and scheduled sessions displayed correctly on the calendar?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'tr_cal_q4', order: 4, text: 'What problems have you encountered while using the Training Calendar, and what improvements would you suggest?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'trainer_profile',
    title: 'Trainer Profile Usability Feedback',
    description: 'Please share your experience viewing and managing your trainer profile to help us improve the LMS.',
    category: 'Trainer',
    lmsFunction: 'Trainer Profile',
    targetRole: 'Trainer',
    questions: [
      { id: 'tr_prof_q1', order: 1, text: 'Can you view your personal and professional information clearly on the Trainer Profile page?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'tr_prof_q2', order: 2, text: 'Are you able to update your email address successfully using the Edit Email function?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'tr_prof_q3', order: 3, text: 'How easy is it to find and manage your profile information?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'tr_prof_q4', order: 4, text: 'What problems have you encountered while viewing or updating your profile, and what improvements would you suggest?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'learner_progress',
    title: 'LMS Learner Dashboard Progress Check',
    category: 'Learner',
    lmsFunction: 'Learner Dashboard Overview',
    targetRole: 'Learner',
    questions: [
      { id: 'lrn_q1', order: 1, text: 'Were you able to find the course or information you needed on the Learner Dashboard?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'lrn_q2', order: 2, text: 'How easy was it to navigate and use the Learner Dashboard?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'lrn_q3', order: 3, text: 'Were your course details, registration status, and available actions clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'lrn_q4', order: 4, text: 'What difficulties did you encounter on the Learner Dashboard, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'learner_course_reg',
    title: 'Learner Course Registration Feedback',
    category: 'Learner',
    lmsFunction: 'Course Registration',
    targetRole: 'Learner',
    questions: [
      { id: 'lrn_reg_q1', order: 1, text: 'Were you able to register for the intended course or session?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'lrn_reg_q2', order: 2, text: 'How easy was it to find a course and complete its registration?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'lrn_reg_q3', order: 3, text: 'Were the course details, registration requirements, and registration status clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No'] },
      { id: 'lrn_reg_q4', order: 4, text: 'What difficulties did you encounter when registering for a course, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'learner_cert_access',
    title: 'Learner Certificate Access Feedback',
    category: 'Learner',
    lmsFunction: 'Certificate Access',
    targetRole: 'Learner',
    questions: [
      { id: 'lrn_cert_q1', order: 1, text: 'Were you able to find and access the certificate you needed, if you were eligible for one?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'lrn_cert_q2', order: 2, text: 'How easy was it to find and access your certificates?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'lrn_cert_q3', order: 3, text: 'Were the certificate details and availability status clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'lrn_cert_q4', order: 4, text: 'What difficulties did you encounter when accessing certificates, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  },
  {
    id: 'learner_self_checkin',
    title: 'Learner Self Check-in Feedback',
    category: 'Learner',
    lmsFunction: 'Learner Self Check-in',
    targetRole: 'Learner',
    questions: [
      { id: 'lrn_chk_q1', order: 1, text: 'Were you able to complete self check-in for the intended class session?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'lrn_chk_q2', order: 2, text: 'How easy was it to enter the attendance code and complete self check-in?', type: 'rating', required: true, options: ['1 — Very difficult', '2 — Difficult', '3 — Neutral', '4 — Easy', '5 — Very easy'] },
      { id: 'lrn_chk_q3', order: 3, text: 'Were the attendance code instructions and check-in confirmation or error messages clear?', type: 'radio', required: true, options: ['Yes', 'Partially', 'No', 'Not applicable'] },
      { id: 'lrn_chk_q4', order: 4, text: 'What difficulties did you encounter during self check-in, or what would you improve?', type: 'text', required: true, options: [] }
    ]
  }
];

export const FeedbackModal = ({
  currentView = 'login',
  user = null,
  isOpen = false,
  onClose = () => {},
  customQuestionnaireId = null,
  roleFilter = null
}) => {
  const [questionnaires, setQuestionnaires] = useState(DEFAULT_QUESTIONNAIRES);
  const [selectedQId, setSelectedQId] = useState('trainer_progress');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Fetch definitions from API if available
  useEffect(() => {
    let isMounted = true;
    fetch('/api/feedback/definitions')
      .then(res => res.json())
      .then(data => {
        if (isMounted && data.status === 'success' && Array.isArray(data.data) && data.data.length > 0) {
          setQuestionnaires(data.data);
        }
      })
      .catch(() => {
        // Fallback to DEFAULT_QUESTIONNAIRES
      });
    return () => { isMounted = false; };
  }, []);

  // Determine effective role filter: default to 'Admin' on report views, 'Trainer' on trainer views
  const effectiveRoleFilter = useMemo(() => {
    if (roleFilter) return roleFilter;
    const view = (currentView || '').toLowerCase();
    if (view.includes('report') || view.includes('adminreports')) {
      return 'Admin';
    }
    if (view.includes('trainer') || (user?.role || '').toLowerCase() === 'trainer') {
      return 'Trainer';
    }
    return null;
  }, [roleFilter, currentView, user]);

  // Filter questionnaires so only the allowed part is shown in the feedback form (Trainer or Admin reports)
  const displayedQuestionnaires = useMemo(() => {
    // If admin_reports is explicitly requested, only show Reporting & Analytics Feedback
    if (customQuestionnaireId === 'admin_reports') {
      const adminReportsOnly = questionnaires.filter(q => q.id === 'admin_reports');
      if (adminReportsOnly.length > 0) return adminReportsOnly;
    }

    if (effectiveRoleFilter && effectiveRoleFilter.toLowerCase() !== 'all') {
      const filtered = questionnaires.filter(
        q => (q.category || '').toLowerCase() === effectiveRoleFilter.toLowerCase() || 
             (q.targetRole || '').toLowerCase() === effectiveRoleFilter.toLowerCase()
      );
      if (filtered.length > 0) {
        if (effectiveRoleFilter.toLowerCase() === 'admin') {
          // For the admin feedback area, hide others first and only show Reporting & Analytics Feedback
          const adminReportsOnly = filtered.filter(q => q.id === 'admin_reports');
          if (adminReportsOnly.length > 0) return adminReportsOnly;
        }
        return filtered;
      }
    }
    return questionnaires;
  }, [questionnaires, effectiveRoleFilter, customQuestionnaireId]);

  // Determine the most appropriate questionnaire based on current route and user role
  const defaultRecommendedQId = useMemo(() => {
    if (customQuestionnaireId) {
      const match = displayedQuestionnaires.find(q => q.id === customQuestionnaireId);
      if (match) return match.id;
    }

    const view = (currentView || '').toLowerCase();

    // If only admin questionnaires are displayed (e.g. for reports)
    if (effectiveRoleFilter && effectiveRoleFilter.toLowerCase() === 'admin') {
      return 'admin_reports';
    }

    // If only trainer questionnaires are displayed
    if (effectiveRoleFilter && effectiveRoleFilter.toLowerCase() === 'trainer') {
      if (view.includes('profile')) {
        return 'trainer_profile';
      }
      if (view.includes('calendar')) {
        return 'trainer_calendar';
      }
      if (view.includes('tracker') || view.includes('monitor')) {
        return 'trainer_att_tracker';
      }
      if (view.includes('attendance') || view.includes('code')) {
        return 'trainer_att_code';
      }
      return 'trainer_progress';
    }

    const role = (user?.role || '').toLowerCase();

    // 1. Registration
    if (view === 'register' || view.includes('registration-pending') || view === 'registrationpending') {
      return 'reg_progress';
    }

    // 2. Login
    if (view === 'login' || view.includes('resetpassword') || view.includes('reset-password')) {
      return 'login_progress';
    }

    // 3. Trainer Dashboard
    if (role === 'trainer' || view.startsWith('trainer')) {
      if (view.includes('profile')) {
        return 'trainer_profile';
      }
      if (view.includes('calendar')) {
        return 'trainer_calendar';
      }
      if (view.includes('attendance') || view.includes('code')) {
        return 'trainer_att_code';
      }
      return 'trainer_progress';
    }

    // 4. Admin Dashboard
    if (role.includes('admin') || role.includes('editor')) {
      if (view.includes('accountregistration') || view.includes('adminaccounts')) {
        return 'admin_registration_mgmt';
      }
      if (view.includes('accountupdate') || view.includes('adminaccountupdates')) {
        return 'admin_account_updates';
      }
      if (view.includes('admincourses') || view.includes('admintags')) {
        return 'admin_course_mgmt';
      }
      if (view.includes('auditlog') || view.includes('adminauditlogs')) {
        return 'admin_audit_logs';
      }
      if (view.includes('report') || view.includes('adminreports')) {
        return 'admin_reports';
      }
      return 'admin_progress';
    }

    // 5. Learner Dashboard
    if (view === 'mycertificates' || view === 'my-certificates') {
      return 'learner_cert_access';
    }
    if (view === 'myregistrations' || view === 'my-registrations') {
      return 'learner_self_checkin';
    }
    if (view === 'calendar') {
      return 'learner_course_reg';
    }
    if (view === 'account' || view === 'profile') {
      return 'learner_progress';
    }

    return displayedQuestionnaires[0]?.id || 'trainer_progress';
  }, [currentView, user, customQuestionnaireId, effectiveRoleFilter, displayedQuestionnaires]);

  // Set selected questionnaire when opened or when view changes
  useEffect(() => {
    setSelectedQId(defaultRecommendedQId);
    setAnswers({});
    setErrorMsg('');
    setIsSuccess(false);
  }, [defaultRecommendedQId, isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const activeQuestionnaire = useMemo(() => {
    return displayedQuestionnaires.find(q => q.id === selectedQId) || displayedQuestionnaires[0] || questionnaires[0];
  }, [displayedQuestionnaires, selectedQId, questionnaires]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validate required questions
    for (const q of activeQuestionnaire.questions) {
      if (q.required) {
        const val = answers[q.id];
        if (val === undefined || val === null || String(val).trim() === '') {
          if (q.type === 'text') {
            setErrorMsg(`Please answer the required question: "${q.text}". Responses consisting only of whitespace are not allowed.`);
          } else {
            setErrorMsg(`Please answer the required question: "${q.text}"`);
          }
          return;
        }
      }
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, value]) => ({
        questionId,
        value: String(value).trim()
      }));

      const payload = {
        questionnaireId: activeQuestionnaire.id,
        lmsFunction: activeQuestionnaire.lmsFunction,
        pageUrl: window.location.pathname || `/${currentView}`,
        actorRole: user?.role || 'Anonymous',
        actorUserId: user?.id || user?.adminId || user?.trainerId || null,
        actorUsername: user?.username || null,
        answers: formattedAnswers
      };

      const res = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': user?.role || 'Anonymous'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setIsSuccess(true);
      } else {
        setErrorMsg(data.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setErrorMsg('Network error. Unable to reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="lms-feedback-overlay" 
      onClick={onClose} 
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="feedback-dialog-title"
    >
      <div 
        className="lms-feedback-card" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="lms-feedback-header">
          <div className="lms-feedback-header-left">
            <div className="lms-feedback-header-icon">
              <SvgIcon name="messageSquare" size={20} />
            </div>
            <div>
              <h2 id="feedback-dialog-title" className="lms-feedback-header-title">
                {activeQuestionnaire.title}
              </h2>
              {activeQuestionnaire.description ? (
                <p className="lms-feedback-header-desc">
                  {activeQuestionnaire.description}
                </p>
              ) : (
                <p className="lms-feedback-header-sub">
                  Function: {activeQuestionnaire.lmsFunction}
                </p>
              )}
            </div>
          </div>
          <button 
            type="button" 
            className="lms-feedback-close-btn" 
            onClick={onClose} 
            aria-label="Close feedback modal"
          >
            &times;
          </button>
        </div>

        {/* Content Body */}
        {isSuccess ? (
          <div className="lms-feedback-success-card">
            <div className="lms-feedback-success-icon">
              <SvgIcon name="checkCircle" size={36} />
            </div>
            <h3 className="lms-feedback-success-title">Feedback Received!</h3>
            <p className="lms-feedback-success-text">
              Thank you for sharing your feedback. Your response has been securely saved and will help us improve the BlackBerry LMS experience.
            </p>
            <button 
              type="button" 
              className="lms-feedback-btn-submit" 
              style={{ marginTop: '16px' }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="lms-feedback-body">
              {/* Optional Topic Switcher */}
              <div className="lms-feedback-topic-selector">
                <span className="lms-feedback-topic-label">Feedback Area:</span>
                <select 
                  className="lms-feedback-select"
                  value={activeQuestionnaire?.id || selectedQId}
                  onChange={(e) => {
                    setSelectedQId(e.target.value);
                    setAnswers({});
                    setErrorMsg('');
                  }}
                  aria-label="Select questionnaire topic"
                >
                  {displayedQuestionnaires.map(q => {
                    const isOptionAdmin = (q.category || '').toLowerCase() === 'admin' || q.id === 'admin_reports';
                    const optionLabel = isOptionAdmin ? q.title : (q.category ? `${q.title} (${q.category})` : q.title);
                    return (
                      <option key={q.id} value={q.id}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Error Banner */}
              {errorMsg && (
                <div className="lms-feedback-error-banner" role="alert">
                  <SvgIcon name="alertCircle" size={18} />
                  <span>{errorMsg}</span>
                </div>
              )}

              {/* Questionnaire Questions */}
              {activeQuestionnaire.questions.map((q, idx) => (
                <div key={q.id} className="lms-feedback-question-block">
                  <label className="lms-feedback-question-title" htmlFor={`q_input_${q.id}`}>
                    <span>{idx + 1}. {q.text}</span>
                    {q.required ? (
                      <span className="lms-feedback-required-mark" title="Required">*</span>
                    ) : (
                      <span className="lms-feedback-optional-mark">(Optional)</span>
                    )}
                  </label>

                  {/* Radio Question */}
                  {q.type === 'radio' && (
                    <div className="lms-feedback-radio-group" role="radiogroup" aria-required={q.required}>
                      {q.options.map(opt => {
                        const isSelected = answers[q.id] === opt;
                        return (
                          <label 
                            key={opt} 
                            className={`lms-feedback-radio-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name={q.id}
                              value={opt}
                              checked={isSelected}
                              onChange={() => handleAnswerChange(q.id, opt)}
                              className="lms-feedback-radio-input"
                              id={`opt_${q.id}_${opt}`}
                            />
                            <span>{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* 1-5 Rating Question */}
                  {q.type === 'rating' && (
                    <div className="lms-feedback-rating-container">
                      <div className="lms-feedback-rating-pills" role="radiogroup" aria-required={q.required}>
                        {[1, 2, 3, 4, 5].map(score => {
                          const isSelected = String(answers[q.id]) === String(score);
                          const labels = {
                            1: 'Very difficult',
                            2: 'Difficult',
                            3: 'Neutral',
                            4: 'Easy',
                            5: 'Very easy'
                          };
                          return (
                            <button
                              key={score}
                              type="button"
                              className={`lms-feedback-rating-pill ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleAnswerChange(q.id, String(score))}
                              aria-checked={isSelected}
                              role="radio"
                            >
                              <span className="lms-feedback-rating-number">{score}</span>
                              <span className="lms-feedback-rating-label">{labels[score]}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Not Applicable option for certificates/check-in where permitted */}
                      {q.options.some(opt => opt.toLowerCase().includes('not applicable')) && (
                        <button
                          type="button"
                          className={`lms-feedback-na-pill ${answers[q.id] === 'Not applicable' ? 'selected' : ''}`}
                          onClick={() => handleAnswerChange(q.id, 'Not applicable')}
                          aria-checked={answers[q.id] === 'Not applicable'}
                          role="radio"
                        >
                          Not applicable
                        </button>
                      )}
                    </div>
                  )}

                  {/* Text Question */}
                  {q.type === 'text' && (
                    <div className="lms-feedback-textarea-container">
                      <textarea
                        id={`q_input_${q.id}`}
                        className="lms-feedback-textarea"
                        placeholder="Share your experience, difficulties, or suggestions for improvement..."
                        value={answers[q.id] || ''}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        rows={3}
                        maxLength={2000}
                        aria-required={q.required}
                      />
                      <div className="lms-feedback-char-count">
                        <span>{(answers[q.id] || '').length} / 2000 characters ({2000 - (answers[q.id] || '').length} remaining)</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Modal Footer Controls */}
            <div className="lms-feedback-footer">
              <button 
                type="button" 
                className="lms-feedback-btn-cancel" 
                onClick={onClose}
                disabled={submitting}
              >
                Close
              </button>
              <button 
                type="submit" 
                className="lms-feedback-btn-submit" 
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const FloatingFeedbackButton = ({ onClick }) => {
  return (
    <button
      type="button"
      className="lms-feedback-fab"
      onClick={onClick}
      aria-label="Open native LMS questionnaire and feedback"
      title="Give Feedback on this LMS page"
    >
      <span className="lms-feedback-fab-icon">
        <SvgIcon name="messageSquare" size={18} />
      </span>
      <span>Feedback</span>
      <span className="lms-feedback-fab-badge" aria-hidden="true" />
    </button>
  );
};

export default FeedbackModal;
